import React, { useEffect, useState, useContext } from 'react'
import styles from './attribute-details.module.scss'
import Button from 'src/components//button/Button'
import Input from 'src/components//input/Input'
import { StateContext } from '../../AppContext'
import { baseApiURL } from 'src/utils/utils'
import { IState } from 'src/Interface'
import axios from 'axios'
import { toast } from 'react-toastify'
import { validString } from 'src/utils/utils'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface IValues {
  description: string,
  code: string
  globalId: number
}

interface IErrors {
  description: boolean
  code: boolean
}

interface IProps {
  attributeId: number
  close?: any
}

const AttributeDetails: React.FunctionComponent<IProps> = ({ close, attributeId }: IProps) => {
  const dispatch = useContext(DispatchContext)
  const [values, setValues] = useState<IValues>({
    description: null,
    code: null,
    globalId: null
  })
  const state: IState = useContext(StateContext)
  const [attribute, setAttribute] = useState<any>({})
  const { cancellablePromise } = useCancellablePromise()
  const [errors, setErrors] = useState<IErrors>({
    description: false,
    code: false,
  })
  const queryClient = useQueryClient()

  const validCode = /^.{2,3}$/
  const validId = /^\d+$/;

  useEffect(() => {
    if (state.user.accessToken && attributeId) {
      cancellablePromise(axios(`${baseApiURL()}/attributes/${attributeId}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result: any) => {
        setAttribute(result.data)
        setValues(result.data)
      }).catch((err: any) => {
        toast(err.response.data.message)
      }))
    }
  }, [attributeId, state.user.accessToken])

  const updateAttributeMutation = useMutation({
    mutationFn: async () => {
      return axios.put(`${baseApiURL()}/attributes/${attributeId}`,
        {
          description: values.description,
          code: values.code,
          globalId: values.globalId
        }, {
        headers: {
          Authorization: `Bearer ${state.user.accessToken}`
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attributes'] })
      toast('Attribute opdateret')
      close()
    },
    onError: (err: any) => {
      toast(err.response?.data?.message || 'Fejl ved opdatering af attribut')
    }
  })

  const updateAttribute = () => {
    if (state.user.accessToken) {
      updateAttributeMutation.mutate()
    }
  }

  const handleInputChange = (fieldName: string) => (value: string) => {
    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
  }

  const deleteAttribute = () => {
    axios.delete(`${baseApiURL()}/attributes/${attributeId}`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        return null
      }).then(() => {
        return axios(`${baseApiURL()}/attributes`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
      }).then((result) => {
         queryClient.invalidateQueries({ queryKey: ['attributes'] })
        dispatch({ type: 'SHOW_MODAL', showModal: false })
        close()
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
  }


  const enableAttribute = () => {
    if (state.user.accessToken) {
      axios.put(`${baseApiURL()}/attributes/${attributeId}/activate`,
        {}, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        return null
      }).then(() => {
        return axios(`${baseApiURL()}/attributes`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
      }).then((result) => {
        queryClient.invalidateQueries({ queryKey: ['attributes'] })
        close()
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
    }
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false

    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }
    setErrors({
      ...errors,
      [fieldName]: error
    })
  }

  return (
    <>
      <header className={styles.header}>
        <h3>{attribute.description}</h3>
        <span className={styles.closeIcon} onClick={close}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </header>
      <div className={styles.container}>
        <Input
          readOnly={!attribute.updatable || attribute.status === 'ARCHIVED'}
          modifiers='tw-mb-3'
          value={values.globalId}
          label='Id *'
          type='text'
          resetError={() => resetError('globalId')}
          handleValidation={() => handleValidation(validId, 'globalId')}
          handleInputChange={() => handleInputChange('globalId')}
          name='id' />
        <Input
          readOnly={!attribute.updatable || attribute.status === 'ARCHIVED'}
          modifiers='mb-3'
          value={values.description}
          label='Label navn *'
          type='text'
          resetError={() => resetError('description')}
          handleValidation={() => handleValidation(validString, 'description')}
          handleInputChange={() => handleInputChange('description')}
          name='name' />
        <Input
          readOnly={!attribute.updatable || attribute.status === 'ARCHIVED'}
          value={values.code}
          label='Kode *'
          type='text'
          resetError={() => resetError('code')}
          handleValidation={() => handleValidation(validCode, 'code')}
          handleInputChange={() => handleInputChange('code')}
          name='code' />

      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' modifiers='tw-mr-auto' handleClick={close} />
        {attribute.status === 'ACTIVE' && <Button type='destructive' label='Fjern' modifiers='tw-mr-5' handleClick={() => dispatch({ type: "SHOW_MODAL", showModal: true, modalType: 'deleteConfirmation', title: 'Fjern attribute', text: '<p>Er du sikker på at du vil fjerne denne attribute?</p> <p>Dette kan ikke fortrydes.</p>', buttonText: 'Fjern attribute', handleDelete: deleteAttribute })} />}
        <Button disabled={!(values.code && values.description) || updateAttributeMutation.isPaused} loading={updateAttributeMutation.isPaused} type='primary' label={attribute.status === 'ARCHIVED' ? 'Aktivér' : 'Tilføj'} handleClick={attribute.status === 'ARCHIVED' ? enableAttribute : updateAttribute} />
      </footer>
    </>
  )
}

export default AttributeDetails
